import { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Edit3,
  Eye,
  BookOpen,
  Clock,
  GripVertical,
  LayoutDashboard,
  LogOut,
  Plus,
  Presentation,
  RefreshCw,
  Save,
  Music,
  Trash2,
  User,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../../auth/useAuth";
import ChurchLogo from "../../../../assets/logos/Church-Logo.png";
import ServiceIcon from "../../../../assets/logos/Service Icon.png";
import GlobalNotificationPill from "../../../../core/components/GlobalNotificationPill";
import PanelLoadingState from "../../../../core/components/PanelLoadingState";
import useResponsive from "../../../../core/hooks/useResponsive";
import { churchMobileNavStyles } from "../../styles/churchMobileNavStyles";
import { fetchOrganizationAccess } from "../../services/churchOrganizationAccessService";
import { clearChurchOrgUserSession } from "../../services/churchOrgUserAuthService";
import { supabase } from "../../services/churchSupabaseClient";
import {
  addHymnBlocksToCurrentService,
  buildSlidesFromServiceItems,
  createClockSlideItem,
  createHymnTitlePageItem,
  createOrderOfServiceItem,
  createPreacherSlideItem,
  createSermonBlockItem,
  createStartServiceSlideItem,
  createThankYouSlideItem,
  deleteQueuedServiceItems,
  deleteServiceItem,
  getCurrentServiceId,
  getItemLabel,
  getItemMeta,
  loadServiceItems,
  syncHymnBlocksWithWeeklyPlan,
  updateOrderOfServiceItem,
  updateServiceItemOrder,
} from "../../services/serviceService";
import { loadUpcomingWeeklyHymnPlan } from "../../services/hymnService";
import { updateChurchLiveDisplay } from "../../services/liveDisplayService";
import { eventPageStyles } from "../Events/EventsPage";

const SERVICE_SECTIONS = [
  { id: "overview", label: "Overview", shortLabel: "Home", icon: LayoutDashboard },
  { id: "order", label: "Order of Service", shortLabel: "Order", icon: Presentation },
  { id: "controller", label: "Service Controller", shortLabel: "Control", icon: Presentation },
  { id: "preview", label: "Preview", shortLabel: "Preview", icon: Eye },
];

const emptyOrderDraft = {
  title: "",
};

const emptyPreacherDraft = {
  name: "",
};

const emptyInvitationDraft = {
  songNumber: "",
  title: "",
};

const INVITATION_SONG_SLOT_INDEX = 6;

function getServiceItemSourceApp(item = {}) {
  return item.sourceApp || item.source_app || "";
}

function getSongSlotLabel(slotIndex) {
  const normalizedSlotIndex = Number(slotIndex) || 1;
  return `Song ${normalizedSlotIndex}`;
}

function getWeeklyHymnSlotLabel(slot = {}, fallbackIndex = 0) {
  return String(slot?.label || "").trim() || getSongSlotLabel(slot.slotIndex || fallbackIndex + 1);
}

function getServiceHymnSlotLabel(item = {}, fallbackIndex = 0) {
  return String(item?.payload?.slotLabel || "").trim() || getSongSlotLabel(item.payload?.slotIndex || item.sort_order || fallbackIndex + 1);
}

function isHymnBlock(item = {}) {
  return item.itemType === "hymn_block" || item.type === "hymn_block";
}

function isHymnTitlePage(item = {}) {
  return item.itemType === "hymn_title_page" || item.type === "hymn_title_page";
}

function isInvitationSongSlot(value = {}) {
  const slotIndex = Number(value?.slotIndex || value?.payload?.slotIndex || 0);
  const slotLabel = String(value?.label || value?.slotLabel || value?.payload?.slotLabel || "").trim().toLowerCase();
  return slotIndex === INVITATION_SONG_SLOT_INDEX || slotLabel.includes("invitation");
}

function isInvitationTitlePageItem(item = {}) {
  return (
    item?.payload?.sourceType === "invitation-song-title-page" ||
    item?.payload?.invitationTitlePage === true
  );
}

function isPreacherSlide(item = {}) {
  return item.itemType === "preacher_slide" || item.type === "preacher_slide";
}

function isSermonBlock(item = {}) {
  return item.itemType === "sermon_block" || item.type === "sermon_block";
}

function getWorkspaceName(user) {
  return (
    user?.churchOrgSession?.account?.name ||
    user?.account?.name ||
    user?.organization?.name ||
    "Church Workspace"
  );
}

function getWorkspaceLogo(user) {
  return (
    user?.churchOrgSession?.account?.logo_url ||
    user?.churchOrgSession?.account?.logoUrl ||
    user?.account?.logo_url ||
    user?.account?.logoUrl ||
    user?.organization?.logo_url ||
    user?.organization?.logoUrl ||
    ChurchLogo
  );
}

export default function ServicePage({ useStandaloneRoutes = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPhone } = useResponsive();
  const [churchAccount, setChurchAccount] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [serviceId, setServiceId] = useState(getCurrentServiceId());
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const [controllerSlideIndex, setControllerSlideIndex] = useState(0);
  const [orderDraft, setOrderDraft] = useState(emptyOrderDraft);
  const [preacherDraft, setPreacherDraft] = useState(emptyPreacherDraft);
  const [invitationDraft, setInvitationDraft] = useState(emptyInvitationDraft);
  const [editingOrderId, setEditingOrderId] = useState("");
  const [weeklyHymnPlan, setWeeklyHymnPlan] = useState(null);
  const [weeklyHymnsLoading, setWeeklyHymnsLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const resolvedChurchAccount =
    churchAccount ||
    user?.churchOrgSession?.account ||
    user?.account ||
    user?.organization ||
    null;
  const workspaceName = resolvedChurchAccount?.name || getWorkspaceName(user);
  const organizationLogoUrl =
    resolvedChurchAccount?.logo_url ||
    resolvedChurchAccount?.logoUrl ||
    getWorkspaceLogo(user);

  const selectedItem =
    items.find((item) => (item.id || item.source_id) === selectedItemId) || items[0];
  const allSlides = useMemo(() => buildSlidesFromServiceItems(items), [items]);
  const slides = useMemo(
    () =>
      selectedItem
        ? allSlides.filter((slide) => slide.parentId === (selectedItem.id || selectedItem.source_id))
        : [],
    [allSlides, selectedItem]
  );
  const selectedSlide = slides[selectedSlideIndex] || slides[0];
  const totalSlides = allSlides.length;
  const serviceOrderItems = useMemo(
    () => items.filter((item) => ["order-of-service", "hymns"].includes(getServiceItemSourceApp(item))),
    [items]
  );
  const linkedHymnBlockSlots = useMemo(
    () => new Set(
      items
        .filter(isHymnBlock)
        .map((item) => Number(item.payload?.slotIndex || 0))
        .filter(Boolean)
    ),
    [items]
  );
  const linkedHymnTitleSlots = useMemo(
    () => new Set(
      items
        .filter((item) => isHymnTitlePage(item) && !isInvitationTitlePageItem(item))
        .map((item) => Number(item.payload?.slotIndex || 0))
        .filter(Boolean)
    ),
    [items]
  );
  const linkedInvitationAnnouncementSlots = useMemo(
    () => new Set(
      items
        .filter((item) => isHymnTitlePage(item) && isInvitationTitlePageItem(item))
        .map((item) => Number(item.payload?.slotIndex || 0))
        .filter(Boolean)
    ),
    [items]
  );
  const weeklyHymnSlots = useMemo(
    () =>
      (weeklyHymnPlan?.slots || [])
        .filter((slot) => slot?.hymnId)
        .sort((left, right) => Number(left.slotIndex || 0) - Number(right.slotIndex || 0)),
    [weeklyHymnPlan?.slots]
  );
  const weeklyHymnPlanSlots = useMemo(
    () =>
      (weeklyHymnPlan?.slots || [])
        .slice()
        .sort((left, right) => Number(left.slotIndex || 0) - Number(right.slotIndex || 0)),
    [weeklyHymnPlan?.slots]
  );
  const hasSermonBlock = useMemo(
    () => items.some(isSermonBlock),
    [items]
  );
  const weeklyHymnSlotCount = weeklyHymnPlan?.slots?.length || 0;
  const weeklyHymnSelectionText = weeklyHymnSlots.length > 0
    ? `${weeklyHymnSlots.length}${weeklyHymnSlotCount ? ` of ${weeklyHymnSlotCount}` : ""} selected`
    : "Not selected";

  async function refreshServiceItems(isRefresh = false) {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError("");

    try {
      const deletedQueueCount = await deleteQueuedServiceItems(user?.id);
      const result = await loadServiceItems(user?.id);
      setServiceId(result.serviceId);
      setItems(result.items);
      setSelectedItemId((current) => {
        const stillExists = result.items.some(
          (item) => (item.id || item.source_id) === current
        );
        return stillExists
          ? current
          : result.items[0]?.id || result.items[0]?.source_id || "";
      });
      setSelectedSlideIndex(0);
      if (isRefresh) {
        setNotice(
          deletedQueueCount > 0
            ? `${deletedQueueCount} queued item${deletedQueueCount === 1 ? "" : "s"} removed.`
            : "Service order refreshed."
        );
      }
    } catch (loadError) {
      console.error("Service page load error:", loadError);
      setError(loadError?.message || "Could not load service order.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    let mounted = true;

    async function loadChurchAccount() {
      if (!user?.id) {
        setChurchAccount(null);
        return;
      }

      const access = await fetchOrganizationAccess(user.id, "church").catch(() => null);
      if (mounted) {
        setChurchAccount(access?.account || null);
      }
    }

    async function loadWeeklyHymns() {
      setWeeklyHymnsLoading(true);
      try {
        const plan = await loadUpcomingWeeklyHymnPlan(user);
        if (mounted) {
          setWeeklyHymnPlan(plan);
          const syncedItems = await syncHymnBlocksWithWeeklyPlan(user?.id, plan);
          if (mounted) {
            setItems(syncedItems);
          }
        }
      } catch (weeklyError) {
        console.error("Weekly hymn plan load error:", weeklyError);
      } finally {
        if (mounted) {
          setWeeklyHymnsLoading(false);
        }
      }
    }

    loadChurchAccount();
    refreshServiceItems(false);
    loadWeeklyHymns();

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  useEffect(() => {
    setSelectedSlideIndex(0);
  }, [selectedItemId]);

  useEffect(() => {
    setControllerSlideIndex((current) => Math.min(current, Math.max(allSlides.length - 1, 0)));
  }, [allSlides.length]);

  function resetOrderDraft() {
    setOrderDraft(emptyOrderDraft);
    setEditingOrderId("");
  }

  function resetPreacherDraft() {
    setPreacherDraft(emptyPreacherDraft);
  }

  function resetInvitationDraft() {
    setInvitationDraft(emptyInvitationDraft);
  }

  function startEditingOrderItem(item) {
    if (getServiceItemSourceApp(item) !== "order-of-service" || isHymnBlock(item) || isHymnTitlePage(item)) return;

    setEditingOrderId(item.id);
    setOrderDraft({
      title: item.title || item.payload?.title || "",
    });
  }

  async function handleSaveOrderItem() {
    const title = orderDraft.title.trim();
    if (!title || busyAction) return;

    setBusyAction(editingOrderId ? "order-edit" : "order-create");
    setError("");
    setNotice("");

    try {
      if (editingOrderId) {
        const item = items.find((currentItem) => currentItem.id === editingOrderId);
        if (!item) return;

        const updated = await updateOrderOfServiceItem(user?.id, item, { title });
        setItems((current) =>
          current.map((currentItem) => (currentItem.id === updated.id ? updated : currentItem))
        );
        setNotice("Order of Service item updated.");
      } else {
        const created = await createOrderOfServiceItem(user?.id, { title });
        setItems((current) => [...current, created].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
        setSelectedItemId(created.id);
        setNotice("Order of Service item created.");
      }

      resetOrderDraft();
    } catch (saveError) {
      console.error("Order of service save error:", saveError);
      setError(saveError?.message || "Could not save Order of Service item.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleAddHymnTitlePage(slot = {}) {
    if (busyAction) return;

    setBusyAction("hymn-title-create");
    setError("");
    setNotice("");

    try {
      const created = await createHymnTitlePageItem(user?.id, slot);
      setItems((current) => [...current, created].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      setSelectedItemId(created.id);
      setNotice(
        isInvitationSongSlot(slot)
          ? "Invitation Song announcement added."
          : `${slot?.slotIndex ? getWeeklyHymnSlotLabel(slot) : "Hymn"} Title page added.`
      );
    } catch (saveError) {
      console.error("Hymn title page save error:", saveError);
      setError(saveError?.message || "Could not add Hymn Title page.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleAddInvitationSongSlide(slot = {}) {
    await handleAddHymnTitlePage({
      ...slot,
      slotIndex: INVITATION_SONG_SLOT_INDEX,
      label: "Invitation Song",
      slotLabel: "Invitation Song",
      invitationTitlePage: true,
    });
    resetInvitationDraft();
  }

  async function handleAddPreacherSlide() {
    const name = preacherDraft.name.trim();
    if (!name || busyAction) return;

    setBusyAction("preacher-create");
    setError("");
    setNotice("");

    try {
      const created = await createPreacherSlideItem(user?.id, { name });
      setItems((current) => [...current, created].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      setSelectedItemId(created.id);
      resetPreacherDraft();
      setNotice("Preacher slide added.");
    } catch (saveError) {
      console.error("Preacher slide save error:", saveError);
      setError(saveError?.message || "Could not add Preacher slide.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleAddSermonBlock() {
    if (busyAction || hasSermonBlock) return;

    setBusyAction("sermon-block-create");
    setError("");
    setNotice("");

    try {
      const created = await createSermonBlockItem(user?.id);
      setItems((current) => [...current, created].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      setSelectedItemId(created.id);
      setNotice("Sermon block added.");
    } catch (saveError) {
      console.error("Sermon block save error:", saveError);
      setError(saveError?.message || "Could not add Sermon block.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleAddClockSlide() {
    if (busyAction) return;

    setBusyAction("clock-slide-create");
    setError("");
    setNotice("");

    try {
      const created = await createClockSlideItem(user?.id);
      setItems((current) => [...current, created].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      setSelectedItemId(created.id);
      setNotice("Clock slide added.");
    } catch (saveError) {
      console.error("Clock slide save error:", saveError);
      setError(saveError?.message || "Could not add Clock slide.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleAddStartServiceSlide() {
    if (busyAction) return;

    setBusyAction("start-service-slide-create");
    setError("");
    setNotice("");

    try {
      const created = await createStartServiceSlideItem(user?.id);
      setItems((current) => [...current, created].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      setSelectedItemId(created.id);
      setNotice("Start Service slide added.");
    } catch (saveError) {
      console.error("Start Service slide save error:", saveError);
      setError(saveError?.message || "Could not add Start Service slide.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleAddThankYouSlide() {
    if (busyAction) return;

    setBusyAction("thank-you-slide-create");
    setError("");
    setNotice("");

    try {
      const created = await createThankYouSlideItem(user?.id);
      setItems((current) => [...current, created].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      setSelectedItemId(created.id);
      setNotice("End of Service slide added.");
    } catch (saveError) {
      console.error("Thank you slide save error:", saveError);
      setError(saveError?.message || "Could not add End of Service slide.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleDeleteOrderItem(item) {
    if (!item?.id || busyAction) return;

    setBusyAction(`order-delete-${item.id}`);
    setError("");
    setNotice("");

    try {
      await deleteServiceItem(user?.id, item.id);
      setItems((current) => current.filter((currentItem) => currentItem.id !== item.id));
      if (selectedItemId === item.id) {
        setSelectedItemId("");
      }
      if (editingOrderId === item.id) {
        resetOrderDraft();
      }
      setNotice("Order of Service item removed.");
    } catch (deleteError) {
      console.error("Order of service delete error:", deleteError);
      setError(deleteError?.message || "Could not remove Order of Service item.");
    } finally {
      setBusyAction("");
    }
  }

  async function moveOrderItem(item, direction) {
    if (!item?.id || busyAction) return;

    const currentIndex = items.findIndex((currentItem) => currentItem.id === item.id);
    const targetIndex = currentIndex + direction;

    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= items.length) return;

    const nextItems = [...items];
    const [moved] = nextItems.splice(currentIndex, 1);
    nextItems.splice(targetIndex, 0, moved);

    setBusyAction(`order-move-${item.id}`);
    setItems(nextItems.map((nextItem, index) => ({ ...nextItem, sort_order: index + 1 })));
    setError("");
    setNotice("");

    try {
      const updated = await updateServiceItemOrder(user?.id, nextItems);
      setItems(updated);
      setNotice("Order of Service order updated.");
    } catch (moveError) {
      console.error("Order of service reorder error:", moveError);
      setError(moveError?.message || "Could not update Order of Service order.");
      refreshServiceItems(false);
    } finally {
      setBusyAction("");
    }
  }

  async function reorderOrderItem(draggedId, targetId) {
    if (!draggedId || !targetId || draggedId === targetId || busyAction) return;

    const currentIndex = items.findIndex((currentItem) => currentItem.id === draggedId);
    const targetIndex = items.findIndex((currentItem) => currentItem.id === targetId);

    if (currentIndex < 0 || targetIndex < 0) return;

    const nextItems = [...items];
    const [moved] = nextItems.splice(currentIndex, 1);
    nextItems.splice(targetIndex, 0, moved);

    setBusyAction(`order-drag-${draggedId}`);
    setItems(nextItems.map((nextItem, index) => ({ ...nextItem, sort_order: index + 1 })));
    setError("");
    setNotice("");

    try {
      const updated = await updateServiceItemOrder(user?.id, nextItems);
      setItems(updated);
      setNotice("Order of Service order updated.");
    } catch (moveError) {
      console.error("Order of service drag reorder error:", moveError);
      setError(moveError?.message || "Could not update Order of Service order.");
      refreshServiceItems(false);
    } finally {
      setBusyAction("");
    }
  }

  async function addWeeklyHymnBlockToOrder(slot) {
    if (busyAction || !slot?.hymnId) return;

    setBusyAction(`add-weekly-hymn-${slot.hymnId}`);
    setError("");
    setNotice("");

    try {
      const plan = weeklyHymnPlan || await loadUpcomingWeeklyHymnPlan(user);
      const createdItems = await addHymnBlocksToCurrentService(user?.id, [slot], plan?.items || []);

      if (createdItems.length === 0) {
        const syncedItems = await syncHymnBlocksWithWeeklyPlan(user?.id, plan);
        setItems(syncedItems);
        setNotice(`${getWeeklyHymnSlotLabel(slot)} is already attached to the service order.`);
        return;
      }

      setItems((current) =>
        [...current, ...createdItems].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      );
      setSelectedItemId(createdItems[0].id);
      setNotice(`${getWeeklyHymnSlotLabel(slot)} block attached to the service order.`);
    } catch (addError) {
      console.error("Weekly hymn add error:", addError);
      setError(addError?.message || "Could not attach weekly hymn block.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleStartMorningService() {
    if (busyAction) return;

    if (allSlides.length === 0) {
      setNotice("Add service slides or hymn blocks before starting Morning Service.");
      return;
    }

    setBusyAction("start-morning-service");
    setError("");
    setNotice("");

    try {
      const plan = await loadUpcomingWeeklyHymnPlan(user).catch(() => null);
      const freshItems = plan
        ? await syncHymnBlocksWithWeeklyPlan(user?.id, plan)
        : items;
      const freshSlides = buildSlidesFromServiceItems(freshItems || []);

      if (freshSlides.length === 0) {
        setNotice("Add service slides or hymn blocks before starting Morning Service.");
        setItems(freshItems || []);
        return;
      }

      setItems(freshItems || []);
      await updateChurchLiveDisplay(user, null, {
        selectedServiceItemId: "all-service-items",
        liveSlides: freshSlides,
        currentSlideIndex: 0,
        isReady: true,
        isLive: true,
        state: "live",
        fast: true,
      });
      setNotice("Morning Service is live on the Live Display.");
    } catch (startError) {
      console.error("Start morning service error:", startError);
      setError(startError?.message || "Could not start Morning Service on Live Display.");
    } finally {
      setBusyAction("");
    }
  }

  async function sendServiceControllerSlide(nextIndex = controllerSlideIndex, actionLabel = "Service slide") {
    if (busyAction || allSlides.length === 0) return;

    const safeIndex = Math.min(Math.max(nextIndex, 0), Math.max(allSlides.length - 1, 0));
    setControllerSlideIndex(safeIndex);
    setBusyAction("service-controller");
    setError("");
    setNotice("");

    try {
      await updateChurchLiveDisplay(user, null, {
        selectedServiceItemId: "all-service-items",
        liveSlides: allSlides,
        currentSlideIndex: safeIndex,
        isReady: true,
        isLive: true,
        state: "live",
        fast: true,
      });
    } catch (controllerError) {
      console.error("Service controller error:", controllerError);
      setError(controllerError?.message || "Could not update Live Display.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleLogout() {
    clearChurchOrgUserSession();
    await supabase.auth.signOut();
    navigate("/church/login", { replace: true });
  }

  if (loading) {
    return (
      <PanelLoadingState
        iconAlt="Service"
        iconComponent={Presentation}
        title="Loading Service"
        detail="Preparing the order of service and slide preview..."
      />
    );
  }

  const showOverview = !useStandaloneRoutes || activeSection === "overview";
  const showOrder = !useStandaloneRoutes || activeSection === "order";
  const showPreview = !useStandaloneRoutes || activeSection === "preview";

  return (
    <div style={useStandaloneRoutes ? { ...styles.wrapper, ...(isPhone ? styles.wrapperPhone : {}) } : styles.page}>
      <div
        style={
          useStandaloneRoutes
            ? { ...styles.managementHeader, ...(isPhone ? styles.managementHeaderPhone : {}) }
            : { ...styles.pageHeader, ...(isPhone ? styles.pageHeaderPhone : {}) }
        }
      >
        {useStandaloneRoutes ? (
          <>
            <span aria-hidden="true" style={styles.managementHeaderShapeOne} />
            <span aria-hidden="true" style={styles.managementHeaderShapeTwo} />
            <div style={{ ...styles.managementHeaderMain, ...(isPhone ? styles.managementHeaderMainPhone : {}) }}>
              <img src={ServiceIcon} alt="Service" style={styles.managementHeaderIcon} />
              <div style={styles.managementHeaderCopy}>
                <div style={styles.managementHeaderEyebrow}>Oikos Church Service</div>
                <div style={styles.managementHeaderName}>{workspaceName}</div>
              </div>
            </div>
            <div style={{ ...styles.managementHeaderMeta, ...(isPhone ? styles.managementHeaderMetaPhone : {}) }}>
              <div style={styles.managementHeaderWelcomeLabel}>Service</div>
              <div style={styles.managementHeaderUserName}>{items.length} items / {totalSlides} slides</div>
              <button
                type="button"
                style={{
                  ...styles.managementHeaderIdRow,
                  ...(isPhone ? styles.managementHeaderIdRowPhone : {}),
                  ...(Boolean(busyAction) || allSlides.length === 0 ? styles.disabledButton : {}),
                }}
                onClick={handleStartMorningService}
                disabled={Boolean(busyAction) || allSlides.length === 0}
              >
                <Presentation size={15} />
                <span>{busyAction === "start-morning-service" ? "Starting..." : "Start Morning Service"}</span>
              </button>
              <button
                type="button"
                style={{ ...styles.managementHeaderIdRow, ...(isPhone ? styles.managementHeaderIdRowPhone : {}) }}
                onClick={() => refreshServiceItems(true)}
                disabled={refreshing || Boolean(busyAction)}
              >
                <RefreshCw size={15} />
                <span>{refreshing ? "Refreshing..." : "Refresh Service"}</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div>
              <h2 style={styles.pageTitle}>Service</h2>
              <div style={styles.pageMeta}>{items.length} items / {totalSlides} slides</div>
            </div>
            <div style={serviceStyles.headerActions}>
              <button
                style={{
                  ...styles.refreshButton,
                  ...(Boolean(busyAction) || allSlides.length === 0 ? styles.disabledButton : {}),
                }}
                onClick={handleStartMorningService}
                disabled={Boolean(busyAction) || allSlides.length === 0}
              >
                {busyAction === "start-morning-service" ? "Starting..." : "Start Morning Service"}
              </button>
              <button
                style={styles.refreshButton}
                onClick={() => refreshServiceItems(true)}
                disabled={refreshing || Boolean(busyAction)}
              >
                {refreshing ? "Refreshing..." : "Refresh Service"}
              </button>
            </div>
          </>
        )}
      </div>

      <GlobalNotificationPill
        message={error || notice}
        onDismiss={() => {
          if (error) setError("");
          else setNotice("");
        }}
        tone={error ? "error" : "success"}
      />

      <div style={useStandaloneRoutes ? styles.eventShell : styles.embeddedShell}>
        {useStandaloneRoutes ? (
          <aside style={{ ...styles.sideNav, ...(isPhone ? serviceStyles.sideNavPhone : {}) }} aria-label="Service sections">
            <div style={{ ...styles.sideNavBrand, ...(isPhone ? styles.sideNavBrandPhone : {}) }}>
              <img src={ChurchLogo} alt="Oikos Church" style={styles.sideNavBrandLogo} />
              <div style={styles.sideNavBrandLine} />
            </div>
            <div style={{ ...styles.sideNavOrg, ...(isPhone ? styles.sideNavOrgPhone : {}) }}>
              <img src={organizationLogoUrl} alt={workspaceName} style={styles.sideNavLogo} />
              <div style={styles.sideNavOrgCopy}>
                <span style={styles.sideNavOrgLabel}>Organization</span>
                <strong style={styles.sideNavOrgName}>{workspaceName}</strong>
              </div>
            </div>
            <div style={{ ...styles.sideNavDivider, ...(isPhone ? styles.sideNavDividerPhone : {}) }} />
            <div style={{ ...styles.sideNavTitle, ...(isPhone ? styles.sideNavTitlePhone : {}) }}>
              Service
            </div>
            <div style={{ ...styles.navMain, ...(isPhone ? styles.navMainPhone : {}) }}>
              {SERVICE_SECTIONS.map((section) => {
                const Icon = section.icon;
                const active = activeSection === section.id;

                return (
                  <button
                    key={section.id}
                    type="button"
                    style={{
                      ...styles.navButton,
                      ...(isPhone ? styles.navButtonPhone : {}),
                      ...(active ? { ...styles.navButtonActive, background: "var(--color-primary)" } : {}),
                      ...(isPhone && active ? styles.navButtonActivePhone : {}),
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.currentTarget.blur();
                      if (section.id === "controller") {
                        navigate("/service/controler");
                        return;
                      }
                      setActiveSection(section.id);
                    }}
                  >
                    <Icon size={isPhone ? 15 : 17} />
                    <span style={isPhone ? styles.navLabelPhone : null}>
                      {isPhone ? section.shortLabel || section.label : section.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ ...styles.sideNavFooter, ...(isPhone ? styles.sideNavFooterPhone : {}) }}>
              <button
                type="button"
                style={{ ...styles.footerButton, ...(isPhone ? styles.footerButtonPhone : {}) }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.currentTarget.blur();
                  navigate("/church");
                }}
              >
                <ArrowLeft size={isPhone ? 15 : 16} />
                <span style={isPhone ? styles.navLabelPhone : null}>Apps</span>
              </button>
              <button
                type="button"
                style={{ ...styles.footerButton, ...styles.logoutButton, ...(isPhone ? styles.footerButtonPhone : {}) }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.currentTarget.blur();
                  handleLogout();
                }}
              >
                <LogOut size={isPhone ? 15 : 16} />
                <span style={isPhone ? styles.navLabelPhone : null}>Logout</span>
              </button>
            </div>
          </aside>
        ) : null}

        <div style={useStandaloneRoutes ? { ...styles.content, ...(isPhone ? styles.contentPhone : {}) } : styles.embeddedContent}>
          {showOverview ? (
            <section style={styles.sectionGrid}>
              <div style={{ ...serviceStyles.infoTileGrid, ...(isPhone ? serviceStyles.infoTileGridPhone : {}) }}>
                <InfoTile label="Order Items" value={serviceOrderItems.length} detail="Slides and blocks in the service order" />
                <InfoTile label="Live Slides" value={totalSlides} detail="Total slides generated for Live Display" tone="green" />
                <InfoTile
                  label="Weekly Hymns"
                  value={weeklyHymnsLoading ? "Loading..." : weeklyHymnSelectionText}
                  detail={weeklyHymnSlots.length ? "This week's hymn selector is ready" : "No hymns selected for this week yet"}
                  tone={weeklyHymnSlots.length ? "green" : "gray"}
                />
                <InfoTile label="Sermon Block" value={hasSermonBlock ? "Added" : "Not added"} detail="iPad sermon viewer return point" tone={hasSermonBlock ? "green" : "gray"} />
                <InfoTile label="Workspace" value={workspaceName} detail="Organization connected to this service" compact />
                <InfoTile label="Service ID" value={serviceId} detail="Current service storage key" tone="gray" compact />
              </div>
            </section>
          ) : null}

          {showOrder ? (
            <section style={styles.sectionGrid}>
              <OrderOfServicePanel
                busyAction={busyAction}
                editingOrderId={editingOrderId}
                isPhone={isPhone}
                items={items}
                onDelete={handleDeleteOrderItem}
                onEdit={startEditingOrderItem}
                onAddWeeklyHymn={addWeeklyHymnBlockToOrder}
                onAddHymnTitlePage={handleAddHymnTitlePage}
                onAddInvitationSongSlide={handleAddInvitationSongSlide}
                onAddClockSlide={handleAddClockSlide}
                onAddStartServiceSlide={handleAddStartServiceSlide}
                onMove={moveOrderItem}
                onReorder={reorderOrderItem}
                onAddPreacherSlide={handleAddPreacherSlide}
                onAddSermonBlock={handleAddSermonBlock}
                onAddThankYouSlide={handleAddThankYouSlide}
                onReset={resetOrderDraft}
                onSave={handleSaveOrderItem}
                orderDraft={orderDraft}
                orderItems={serviceOrderItems}
                preacherDraft={preacherDraft}
                invitationDraft={invitationDraft}
                hasSermonBlock={hasSermonBlock}
                linkedHymnTitleSlots={linkedHymnTitleSlots}
                linkedInvitationAnnouncementSlots={linkedInvitationAnnouncementSlots}
                setPreacherDraft={setPreacherDraft}
                setInvitationDraft={setInvitationDraft}
                setOrderDraft={setOrderDraft}
                linkedHymnBlockSlots={linkedHymnBlockSlots}
                weeklyHymnsLoading={weeklyHymnsLoading}
                weeklyHymnPlanSlots={weeklyHymnPlanSlots}
                weeklyHymnSlots={weeklyHymnSlots}
              />
            </section>
          ) : null}

          {showPreview ? (
            <section style={styles.sectionGrid}>
              <PreviewPanel
                isPhone={isPhone}
                selectedItem={selectedItem}
                selectedSlide={selectedSlide}
                selectedSlideIndex={selectedSlideIndex}
                setSelectedSlideIndex={setSelectedSlideIndex}
                slides={slides}
              />
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function OrderOfServicePanel({
  busyAction,
  editingOrderId,
  isPhone,
  items,
  linkedHymnBlockSlots,
  linkedHymnTitleSlots,
  linkedInvitationAnnouncementSlots,
  onAddWeeklyHymn,
  onDelete,
  onEdit,
  onAddInvitationSongSlide,
  onAddHymnTitlePage,
  onAddClockSlide,
  onAddStartServiceSlide,
  onAddPreacherSlide,
  onAddSermonBlock,
  onAddThankYouSlide,
  onMove,
  onReorder,
  onReset,
  onSave,
  orderDraft,
  orderItems,
  preacherDraft,
  invitationDraft,
  hasSermonBlock,
  setOrderDraft,
  setInvitationDraft,
  setPreacherDraft,
  weeklyHymnsLoading,
  weeklyHymnPlanSlots,
  weeklyHymnSlots,
}) {
  const isSaving = busyAction === "order-create" || busyAction === "order-edit";
  const [draggedItemId, setDraggedItemId] = useState("");
  const [dragOverItemId, setDragOverItemId] = useState("");
  const [selectedHymnBlockSlot, setSelectedHymnBlockSlot] = useState("");
  const selectedHymnBlock =
    weeklyHymnSlots.find((slot) => String(slot.slotIndex) === selectedHymnBlockSlot) ||
    weeklyHymnSlots.find((slot) => !linkedHymnBlockSlots.has(Number(slot.slotIndex))) ||
    weeklyHymnSlots[0] ||
    null;
  const [selectedHymnTitleSlot, setSelectedHymnTitleSlot] = useState("");
  const selectedHymnTitle =
    weeklyHymnSlots.find((slot) => String(slot.slotIndex) === selectedHymnTitleSlot) ||
    weeklyHymnSlots.find((slot) => !linkedHymnTitleSlots.has(Number(slot.slotIndex))) ||
    weeklyHymnSlots[0] ||
    null;
  const selectedHymnBlockLinked = selectedHymnBlock
    ? linkedHymnBlockSlots.has(Number(selectedHymnBlock.slotIndex))
    : false;
  const selectedHymnTitleLinked = selectedHymnTitle
    ? linkedHymnTitleSlots.has(Number(selectedHymnTitle.slotIndex))
    : false;
  const invitationSongSlot =
    (weeklyHymnPlanSlots || []).find(isInvitationSongSlot) ||
    (weeklyHymnPlanSlots || []).find((slot) => Number(slot.slotIndex) === INVITATION_SONG_SLOT_INDEX) ||
    weeklyHymnSlots.find(isInvitationSongSlot) ||
    weeklyHymnSlots.find((slot) => Number(slot.slotIndex) === INVITATION_SONG_SLOT_INDEX) ||
    {
      slotIndex: INVITATION_SONG_SLOT_INDEX,
      label: "Invitation Song",
      slotLabel: "Invitation Song",
      hymnId: "",
      hymn: null,
    };
  const invitationSongReady = Boolean(invitationSongSlot?.hymnId || invitationSongSlot?.hymn?.id);
  const invitationAnnouncementLinked = invitationSongSlot
    ? linkedInvitationAnnouncementSlots.has(Number(invitationSongSlot.slotIndex))
    : false;
  const invitationSongTitle = invitationSongSlot?.hymn?.title || "";
  const invitationSongNumber = invitationSongSlot?.hymn?.songNumber || invitationSongSlot?.hymn?.song_number || "";
  const invitationDraftNumber = invitationDraft.songNumber.trim();
  const invitationAnnouncementNumber = invitationDraftNumber || invitationSongNumber;
  const invitationSongSummary = invitationAnnouncementNumber
    ? `#${invitationAnnouncementNumber} will show under Invitation Song.`
    : invitationSongReady
      ? "The selected Invitation Song does not have a song number yet."
      : "Select the Invitation Song in Weekly Hymns.";
  const invitationSlideSlot = {
    ...invitationSongSlot,
    slotIndex: INVITATION_SONG_SLOT_INDEX,
    label: "Invitation Song",
    slotLabel: "Invitation Song",
    hymnTitle: invitationSongTitle,
    songNumber: invitationDraftNumber || invitationSongNumber,
  };
  const invitationSlideReady = Boolean(invitationAnnouncementNumber);

  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>
        <div>
          <div style={styles.panelTitle}>Order of Service</div>
          <div style={styles.panelSubtle}>Create service slides and attach hymn blocks that follow the weekly selector</div>
        </div>
        <div style={serviceStyles.orderHeaderActions}>
          {editingOrderId ? (
            <button type="button" style={styles.ghostButton} onClick={onReset}>
              Cancel Edit
            </button>
          ) : null}
        </div>
      </div>

      <div style={serviceStyles.builderTile}>
        <div style={serviceStyles.builderTileHeader}>
          <div>
            <div style={serviceStyles.builderTileTitle}>Service Building Tools</div>
            <div style={serviceStyles.builderTileMeta}>Add slides, hymn blocks, hymn title pages, preacher slides, and sermon space to the service order.</div>
          </div>
        </div>
        <div style={{ ...serviceStyles.builderTools, ...(isPhone ? serviceStyles.builderToolsPhone : {}) }}>
          <div style={serviceStyles.builderTool}>
            <BuilderToolHeader icon={Plus} title="Service Slide" description="Add a simple one-line slide for moments like prayer, welcome, offering, or dismissal." />
            <div style={{ ...serviceStyles.builderToolControl, ...(isPhone ? serviceStyles.builderToolControlPhone : {}) }}>
              <input
                style={styles.input}
                placeholder="Opening Prayer"
                value={orderDraft.title}
                onChange={(event) => setOrderDraft({ title: event.target.value })}
              />
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...serviceStyles.builderToolButton,
                  ...(!orderDraft.title.trim() || busyAction ? serviceStyles.builderToolUnavailable : {}),
                }}
                onClick={onSave}
                disabled={!orderDraft.title.trim() || Boolean(busyAction)}
              >
                {editingOrderId ? <Save size={17} /> : <Plus size={17} />}
                {isSaving ? "Saving..." : editingOrderId ? "Save" : "Add"}
              </button>
            </div>
          </div>

          <div style={serviceStyles.builderTool}>
            <BuilderToolHeader
              icon={Presentation}
              title="Invitation Song Announcement"
              description="Add a simple announcement slide with the invitation song number."
            />
            <div style={{ ...serviceStyles.builderToolControl, ...(isPhone ? serviceStyles.builderToolControlPhone : {}) }}>
              <input
                style={styles.input}
                placeholder={invitationSongNumber ? `#${invitationSongNumber}` : "Song #"}
                value={invitationDraft.songNumber}
                onChange={(event) =>
                  setInvitationDraft((current) => ({
                    ...current,
                    songNumber: event.target.value,
                  }))
                }
              />
            </div>
            <div style={serviceStyles.builderToolSingleAction}>
              <div style={serviceStyles.builderToolSummary}>{invitationSongSummary}</div>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...serviceStyles.builderToolButton,
                  ...(!invitationSlideReady || invitationAnnouncementLinked || busyAction ? serviceStyles.builderToolUnavailable : {}),
                }}
                onClick={() => invitationSlideReady && onAddInvitationSongSlide(invitationSlideSlot)}
                disabled={!invitationSlideReady || invitationAnnouncementLinked || Boolean(busyAction)}
              >
                <Presentation size={17} />
                {invitationAnnouncementLinked ? "Added" : busyAction === "hymn-title-create" ? "Adding..." : "Add Announcement"}
              </button>
            </div>
          </div>

          <div style={serviceStyles.builderTool}>
            <BuilderToolHeader icon={Music} title="Hymn Block" description="Insert the selected weekly hymn with its full-screen hymn image slides." />
            <div style={{ ...serviceStyles.builderToolControl, ...(isPhone ? serviceStyles.builderToolControlPhone : {}) }}>
              <select
                style={styles.input}
                value={selectedHymnBlock?.slotIndex || ""}
                onChange={(event) => setSelectedHymnBlockSlot(event.target.value)}
              >
                {weeklyHymnSlots.length === 0 ? (
                  <option value="">No weekly hymns selected</option>
                ) : (
                  weeklyHymnSlots.map((slot) => {
                    const hymnTitle = slot.hymn?.title || "Selected hymn";
                    const songNumber = slot.hymn?.songNumber || slot.hymn?.song_number || "";
                    const attached = linkedHymnBlockSlots.has(Number(slot.slotIndex));
                    const label = [
                      getWeeklyHymnSlotLabel(slot),
                      [songNumber ? `#${songNumber}` : "", hymnTitle].filter(Boolean).join(" "),
                      attached ? "attached" : "",
                    ].filter(Boolean).join(" - ");

                    return (
                      <option key={`${slot.slotIndex}-${slot.hymnId}`} value={slot.slotIndex}>
                        {label}
                      </option>
                    );
                  })
                )}
              </select>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...serviceStyles.builderToolButton,
                  ...(!selectedHymnBlock || selectedHymnBlockLinked || busyAction ? serviceStyles.builderToolUnavailable : {}),
                }}
                onClick={() => selectedHymnBlock && onAddWeeklyHymn(selectedHymnBlock)}
                disabled={!selectedHymnBlock || selectedHymnBlockLinked || Boolean(busyAction)}
              >
                <Music size={17} />
                {selectedHymnBlockLinked ? "Added" : "Add"}
              </button>
            </div>
          </div>

          <div style={serviceStyles.builderTool}>
            <BuilderToolHeader icon={Presentation} title="Hymn Title" description="Add a title page that pulls the song name and number from a weekly hymn block." />
            <div style={{ ...serviceStyles.builderToolControl, ...(isPhone ? serviceStyles.builderToolControlPhone : {}) }}>
              <select
                style={styles.input}
                value={selectedHymnTitle?.slotIndex || ""}
                onChange={(event) => setSelectedHymnTitleSlot(event.target.value)}
              >
                {weeklyHymnSlots.length === 0 ? (
                  <option value="">No weekly hymns selected</option>
                ) : (
                  weeklyHymnSlots.map((slot) => {
                    const hymnTitle = slot.hymn?.title || "Selected hymn";
                    const songNumber = slot.hymn?.songNumber || slot.hymn?.song_number || "";
                    const added = linkedHymnTitleSlots.has(Number(slot.slotIndex));
                    const label = [
                      `${getWeeklyHymnSlotLabel(slot)} title`,
                      [songNumber ? `#${songNumber}` : "", hymnTitle].filter(Boolean).join(" "),
                      added ? "added" : "",
                    ].filter(Boolean).join(" - ");

                    return (
                      <option key={`title-${slot.slotIndex}-${slot.hymnId}`} value={slot.slotIndex}>
                        {label}
                      </option>
                    );
                  })
                )}
              </select>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...serviceStyles.builderToolButton,
                  ...(!selectedHymnTitle || selectedHymnTitleLinked || busyAction ? serviceStyles.builderToolUnavailable : {}),
                }}
                onClick={() => selectedHymnTitle && onAddHymnTitlePage(selectedHymnTitle)}
                disabled={!selectedHymnTitle || selectedHymnTitleLinked || Boolean(busyAction)}
              >
                <Presentation size={17} />
                {selectedHymnTitleLinked ? "Added" : "Add"}
              </button>
            </div>
          </div>

          <div style={serviceStyles.builderTool}>
            <BuilderToolHeader icon={User} title="Preacher" description="Add a slide with the preacher name before the sermon section." />
            <div style={{ ...serviceStyles.builderToolControl, ...(isPhone ? serviceStyles.builderToolControlPhone : {}) }}>
              <input
                style={styles.input}
                placeholder="Pastor Name"
                value={preacherDraft.name}
                onChange={(event) => setPreacherDraft({ name: event.target.value })}
              />
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...serviceStyles.builderToolButton,
                  ...(!preacherDraft.name.trim() || busyAction ? serviceStyles.builderToolUnavailable : {}),
                }}
                onClick={onAddPreacherSlide}
                disabled={!preacherDraft.name.trim() || Boolean(busyAction)}
              >
                <User size={17} />
                {busyAction === "preacher-create" ? "Adding..." : "Add"}
              </button>
            </div>
          </div>

          <div style={serviceStyles.builderTool}>
            <BuilderToolHeader icon={BookOpen} title="Sermon" description="Reserve the spot where the iPad sermon viewer takes over and returns after sermon." />
            <div style={serviceStyles.builderToolSingleAction}>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...serviceStyles.builderToolButton,
                  ...(hasSermonBlock || busyAction ? serviceStyles.builderToolUnavailable : {}),
                }}
                onClick={onAddSermonBlock}
                disabled={hasSermonBlock || Boolean(busyAction)}
              >
                <BookOpen size={17} />
                {hasSermonBlock ? "Added" : busyAction === "sermon-block-create" ? "Adding..." : "Add Sermon Block"}
              </button>
            </div>
          </div>

          <div style={serviceStyles.builderTool}>
            <BuilderToolHeader icon={Presentation} title="Start Service" description="Add a welcome slide with the organization logo and name." />
            <div style={serviceStyles.builderToolSingleAction}>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...serviceStyles.builderToolButton,
                  ...(busyAction ? serviceStyles.builderToolUnavailable : {}),
                }}
                onClick={onAddStartServiceSlide}
                disabled={Boolean(busyAction)}
              >
                <Presentation size={17} />
                {busyAction === "start-service-slide-create" ? "Adding..." : "Add Start"}
              </button>
            </div>
          </div>

          <div style={serviceStyles.builderTool}>
            <BuilderToolHeader icon={Presentation} title="End of Service" description="Add a closing slide with the organization logo and thank-you message." />
            <div style={serviceStyles.builderToolSingleAction}>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...serviceStyles.builderToolButton,
                  ...(busyAction ? serviceStyles.builderToolUnavailable : {}),
                }}
                onClick={onAddThankYouSlide}
                disabled={Boolean(busyAction)}
              >
                <Presentation size={17} />
                {busyAction === "thank-you-slide-create" ? "Adding..." : "Add End"}
              </button>
            </div>
          </div>

          <div style={serviceStyles.builderTool}>
            <BuilderToolHeader icon={Clock} title="Clock" description="Add a live clock slide for transitions or pauses in the service." />
            <div style={serviceStyles.builderToolSingleAction}>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...serviceStyles.builderToolButton,
                  ...(busyAction ? serviceStyles.builderToolUnavailable : {}),
                }}
                onClick={onAddClockSlide}
                disabled={Boolean(busyAction)}
              >
                <Clock size={17} />
                {busyAction === "clock-slide-create" ? "Adding..." : "Add Clock"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={serviceStyles.currentOrderHeader}>
        <div>
          <div style={serviceStyles.currentOrderTitle}>Current Order of Service</div>
          <div style={serviceStyles.currentOrderMeta}>Drag items to reorder the live service flow.</div>
        </div>
        <div style={serviceStyles.currentOrderCount}>{orderItems.length} item{orderItems.length === 1 ? "" : "s"}</div>
      </div>

      {orderItems.length === 0 ? (
        <div style={styles.emptyState}>
          No service order lines or hymns yet. Add a line above or add a weekly hymn block.
        </div>
      ) : (
        <div style={styles.cardList}>
          {orderItems.map((item) => {
            const itemIndex = items.findIndex((currentItem) => currentItem.id === item.id);
            const sourceApp = getServiceItemSourceApp(item);
            const isHymn = sourceApp === "hymns";
            const isBlock = isHymnBlock(item);
            const isTitlePage = isHymnTitlePage(item);
            const isInvitationTitlePage = isTitlePage && isInvitationTitlePageItem(item);
            const isPreacher = isPreacherSlide(item);
            const isSermon = isSermonBlock(item);
            const title = item.title || item.payload?.title || "Service Slide";
            const songNumber = item.payload?.songNumber || "";
            const hymnBlockTitle = [songNumber ? `#${songNumber}` : "", item.payload?.hymnTitle || ""].filter(Boolean).join(" ");
            const displayTitle = isSermon
              ? "Sermon"
              : isPreacher
              ? "Preacher"
              : isTitlePage
              ? isInvitationTitlePage
                ? "Invitation Song Announcement"
                : item.payload?.title || "Hymn Title page"
              : isHymn
              ? [songNumber ? `#${songNumber}` : "", title].filter(Boolean).join(" ")
              : isBlock
                ? item.payload?.slotLabel || title
              : title;
            const displayMeta = isSermon
              ? "iPad sermon viewer takes over here; return resumes after this block"
              : isPreacher
              ? (item.payload?.body || "Preacher slide")
              : isTitlePage
              ? isInvitationTitlePage
                ? [item.payload?.songNumber ? `#${item.payload.songNumber}` : "", "shown before the invitation song"].filter(Boolean).join(" ")
                : (item.payload?.slotLabel
                ? `Pulls ${item.payload.slotLabel} name and number`
                : "Pulls the song name and number from the hymn below it")
              : isHymn
              ? getServiceHymnSlotLabel(item, itemIndex)
              : isBlock
                ? (hymnBlockTitle ? `Pulls ${hymnBlockTitle} from the hymn selector` : "Pulls from the hymn selector")
                : "Service Slide";

            return (
              <article
                key={item.id}
                draggable={!busyAction}
                onDragStart={(event) => {
                  setDraggedItemId(item.id);
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", item.id);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  if (!busyAction && dragOverItemId !== item.id) {
                    setDragOverItemId(item.id);
                  }
                }}
                onDragLeave={() => {
                  if (dragOverItemId === item.id) {
                    setDragOverItemId("");
                  }
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  const sourceId = event.dataTransfer.getData("text/plain") || draggedItemId;
                  setDraggedItemId("");
                  setDragOverItemId("");
                  onReorder(sourceId, item.id);
                }}
                onDragEnd={() => {
                  setDraggedItemId("");
                  setDragOverItemId("");
                }}
                style={{
                  ...serviceStyles.orderCard,
                  ...(dragOverItemId === item.id && draggedItemId !== item.id ? serviceStyles.orderCardDropTarget : {}),
                  ...(draggedItemId === item.id ? serviceStyles.orderCardDragging : {}),
                }}
              >
                <div style={serviceStyles.orderMain}>
                  <span style={serviceStyles.dragHandle} title="Drag to reorder">
                    <GripVertical size={18} />
                  </span>
                  <span style={serviceStyles.queueNumber}>{String(itemIndex + 1).padStart(2, "0")}</span>
                  <span style={serviceStyles.queueCopy}>
                    <span style={serviceStyles.queueTitle}>{displayTitle}</span>
                    <span style={serviceStyles.queueMeta}>
                      {displayMeta}
                    </span>
                  </span>
                </div>
                <div style={serviceStyles.slideActions}>
                  <button
                    type="button"
                    style={styles.iconButton}
                    onClick={() => onMove(item, -1)}
                    disabled={Boolean(busyAction) || itemIndex <= 0}
                  >
                    <ArrowUp size={15} />
                    <span>Up</span>
                  </button>
                  <button
                    type="button"
                    style={styles.iconButton}
                    onClick={() => onMove(item, 1)}
                    disabled={Boolean(busyAction) || itemIndex >= items.length - 1}
                  >
                    <ArrowDown size={15} />
                    <span>Down</span>
                  </button>
                  <button
                    type="button"
                    style={styles.iconButton}
                    onClick={() => onEdit(item)}
                    disabled={Boolean(busyAction) || isHymn || isBlock || isTitlePage || isPreacher || isSermon}
                  >
                    <Edit3 size={15} />
                    <span>Edit</span>
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.iconButton, ...styles.dangerButton }}
                    onClick={() => onDelete(item)}
                    disabled={Boolean(busyAction)}
                  >
                    <Trash2 size={15} />
                    <span>Remove</span>
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ compact = false, label, value, tone = "blue" }) {
  return (
    <div style={{ ...styles.statCard, ...(tone === "green" ? styles.statGreen : tone === "gray" ? styles.statGray : {}) }}>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, ...(compact ? serviceStyles.statValueCompact : {}) }}>{value}</div>
    </div>
  );
}

function BuilderToolHeader({ description, icon: Icon, title }) {
  return (
    <div style={serviceStyles.builderToolHeader}>
      <div style={serviceStyles.builderToolTitleRow}>
        <Icon size={15} />
        <span>{title}</span>
      </div>
      <div style={serviceStyles.builderToolDescription}>{description}</div>
    </div>
  );
}

function InfoTile({ compact = false, detail, label, tone = "blue", value }) {
  return (
    <div style={{ ...serviceStyles.infoTile, ...(tone === "green" ? serviceStyles.infoTileGreen : tone === "gray" ? serviceStyles.infoTileGray : {}) }}>
      <div style={serviceStyles.infoTileLabel}>{label}</div>
      <div style={{ ...serviceStyles.infoTileValue, ...(compact ? serviceStyles.infoTileValueCompact : {}) }}>{value}</div>
      <div style={serviceStyles.infoTileDetail}>{detail}</div>
    </div>
  );
}

function EmptyService() {
  return (
    <div style={styles.emptyState}>
      Add a service slide or weekly hymn block to begin building the order of service.
    </div>
  );
}

function ServiceControllerPanel({
  busyAction,
  controllerSlideIndex,
  isPhone,
  onBack,
  onNext,
  onReturn,
  slides,
}) {
  const slide = slides[controllerSlideIndex] || slides[0] || null;
  const slideCount = slides.length;
  const isBusy = Boolean(busyAction);

  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>
        <div>
          <div style={styles.panelTitle}>Service Controller</div>
          <div style={styles.panelSubtle}>Control the Morning Service slide show on the Live Display.</div>
        </div>
        <span style={serviceStyles.slideCounter}>
          Slide {slideCount ? controllerSlideIndex + 1 : 0} of {slideCount}
        </span>
      </div>

      <div style={{ ...serviceStyles.controllerScreen, ...(isPhone ? serviceStyles.controllerScreenPhone : {}) }}>
        {slide ? <SlidePreview slide={slide} /> : <div style={serviceStyles.previewEmpty}>No service slides ready</div>}
      </div>

      <div style={{ ...serviceStyles.controllerBottomControls, ...(isPhone ? serviceStyles.controllerBottomControlsPhone : {}) }}>
        <button
          type="button"
          style={serviceStyles.controllerBottomButton}
          onClick={onBack}
          disabled={isBusy || controllerSlideIndex <= 0 || slideCount === 0}
        >
          <ArrowLeft size={26} strokeWidth={2.5} />
          Back
        </button>
        <button
          type="button"
          style={serviceStyles.controllerBottomPrimaryButton}
          onClick={onReturn}
          disabled={isBusy || slideCount === 0}
        >
          <Presentation size={18} />
          Return to Service Loop
        </button>
        <button
          type="button"
          style={serviceStyles.controllerBottomButton}
          onClick={onNext}
          disabled={isBusy || controllerSlideIndex >= slideCount - 1 || slideCount === 0}
        >
          Next
          <ArrowRightIcon />
        </button>
      </div>
    </div>
  );
}

function ArrowRightIcon() {
  return <ArrowLeft size={26} strokeWidth={2.5} style={{ transform: "rotate(180deg)" }} />;
}

function PreviewPanel({ isPhone, selectedItem, selectedSlide, selectedSlideIndex, setSelectedSlideIndex, slides }) {
  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>
        <div>
          <div style={styles.panelTitle}>{selectedItem ? getItemLabel(selectedItem) : "Slide Preview"}</div>
          <div style={styles.panelSubtle}>{selectedItem ? getItemMeta(selectedItem) : "Choose a service item to preview slides"}</div>
        </div>
        {slides.length > 1 ? (
          <div style={{ ...serviceStyles.previewControls, ...(isPhone ? serviceStyles.previewControlsPhone : {}) }}>
            <button
              type="button"
              style={styles.ghostButton}
              onClick={() => setSelectedSlideIndex((current) => Math.max(0, current - 1))}
              disabled={selectedSlideIndex === 0}
            >
              Prev
            </button>
            <span style={serviceStyles.slideCounter}>Slide {selectedSlideIndex + 1} of {slides.length}</span>
            <button
              type="button"
              style={styles.ghostButton}
              onClick={() => setSelectedSlideIndex((current) => Math.min(slides.length - 1, current + 1))}
              disabled={selectedSlideIndex >= slides.length - 1}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
      <div style={{ ...serviceStyles.screenPreview, ...(isPhone ? serviceStyles.screenPreviewPhone : {}) }}>
        {selectedSlide ? <SlidePreview slide={selectedSlide} /> : <div style={serviceStyles.previewEmpty}>No slide selected</div>}
      </div>
    </div>
  );
}

function SlidePreview({ slide }) {
  if (slide.itemType === "title_slide") {
    return (
      <div style={serviceStyles.titleSlide}>
        <div style={serviceStyles.titleHeadline}>{slide.title}</div>
        <div style={serviceStyles.titleSubline}>{slide.subtitle}</div>
        <div style={serviceStyles.titleDate}>{slide.date}</div>
      </div>
    );
  }

  if (slide.itemType === "verse") {
    return (
      <div style={serviceStyles.verseSlide}>
        <div style={serviceStyles.verseReference}>{slide.title}</div>
        <div style={serviceStyles.verseText}>
          {slide.verseNumber ? <span style={serviceStyles.verseNumber}>{slide.verseNumber}</span> : null}
          <span>{slide.text}</span>
        </div>
      </div>
    );
  }

  if (slide.itemType === "hymn" && slide.imageUrl) {
    return <img src={slide.imageUrl} alt={slide.title || "Hymn slide"} style={serviceStyles.mediaSlide} />;
  }

  return (
    <div style={serviceStyles.customSlide}>
      <div style={serviceStyles.customTitle}>
        {slide.songNumber ? `#${slide.songNumber} ${slide.title}` : slide.title}
      </div>
      <div style={serviceStyles.customBody}>{slide.body}</div>
    </div>
  );
}

const styles = eventPageStyles;

const serviceStyles = {
  ...eventPageStyles,
  sideNavPhone: {
    ...churchMobileNavStyles.dock,
    gridAutoRows: "minmax(54px, auto)",
    gridTemplateColumns: `repeat(${SERVICE_SECTIONS.length + 2}, minmax(0, 1fr))`,
  },
  statValueCompact: {
    fontSize: 21,
    lineHeight: 1.15,
    overflowWrap: "anywhere",
  },
  infoTileGrid: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  },
  infoTileGridPhone: {
    gridTemplateColumns: "1fr",
  },
  infoTile: {
    background: "#ffffff",
    border: "1px solid #cfe0ef",
    borderRadius: 18,
    boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
    display: "grid",
    gap: 8,
    minWidth: 0,
    padding: 18,
  },
  infoTileGreen: {
    background: "#f0fdf4",
    borderColor: "#bbf7d0",
  },
  infoTileGray: {
    background: "#f8fafc",
    borderColor: "#e2e8f0",
  },
  infoTileLabel: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  infoTileValue: {
    color: "#0f172a",
    fontSize: "clamp(25px, 3vw, 42px)",
    fontWeight: 950,
    lineHeight: 1.02,
    overflowWrap: "anywhere",
  },
  infoTileValueCompact: {
    fontSize: "clamp(18px, 2vw, 28px)",
    lineHeight: 1.12,
  },
  infoTileDetail: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.35,
  },
  headerActions: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "flex-end",
  },
  builderTile: {
    background: "#ffffff",
    border: "1px solid #cbd8cf",
    borderRadius: 18,
    boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
    display: "grid",
    gap: 12,
    padding: 14,
  },
  builderTileHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
  },
  builderTileTitle: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 950,
  },
  builderTileMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
    marginTop: 3,
  },
  builderTools: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  },
  builderToolsPhone: {
    gridTemplateColumns: "1fr",
  },
  builderTool: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    display: "grid",
    gap: 12,
    gridTemplateRows: "auto 1fr auto",
    minHeight: 190,
    minWidth: 0,
    padding: 12,
  },
  builderToolAction: {
    alignContent: "space-between",
  },
  builderToolHeader: {
    display: "grid",
    gap: 5,
    minHeight: 58,
  },
  builderToolTitleRow: {
    alignItems: "center",
    color: "#0f172a",
    display: "flex",
    fontSize: 12,
    fontWeight: 950,
    gap: 7,
    textTransform: "uppercase",
  },
  builderToolDescription: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 750,
    lineHeight: 1.35,
    textTransform: "none",
  },
  builderToolControl: {
    alignItems: "stretch",
    alignSelf: "end",
    display: "grid",
    gap: 8,
    gridTemplateColumns: "minmax(0, 1fr) minmax(96px, auto)",
  },
  builderToolControlPhone: {
    gridTemplateColumns: "1fr",
  },
  builderToolSingleAction: {
    alignItems: "stretch",
    alignSelf: "end",
    display: "grid",
    gap: 8,
  },
  builderToolSummary: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 850,
    lineHeight: 1.35,
    minHeight: 18,
    overflowWrap: "anywhere",
  },
  orderHeaderActions: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
  },
  builderToolButton: {
    alignSelf: "stretch",
    minHeight: 44,
    whiteSpace: "nowrap",
    width: "100%",
  },
  builderToolUnavailable: {
    background: "var(--color-primary)",
    border: "none",
    color: "#ffffff",
    cursor: "not-allowed",
    filter: "none",
    opacity: 1,
  },
  currentOrderHeader: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #cbd8cf",
    borderRadius: 18,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    padding: "14px 16px",
  },
  currentOrderTitle: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 950,
  },
  currentOrderMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
    marginTop: 3,
  },
  currentOrderCount: {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 999,
    color: "#166534",
    fontSize: 12,
    fontWeight: 950,
    padding: "8px 11px",
    whiteSpace: "nowrap",
  },
  orderCard: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    display: "grid",
    gap: 12,
    gridTemplateColumns: "minmax(0, 1fr) auto",
    padding: 14,
  },
  orderCardDragging: {
    opacity: 0.55,
  },
  orderCardDropTarget: {
    borderColor: "var(--color-primary)",
    boxShadow: "0 0 0 2px rgba(var(--color-primary-rgb), 0.18)",
  },
  orderMain: {
    alignItems: "center",
    display: "grid",
    gap: 12,
    gridTemplateColumns: "24px 40px minmax(0, 1fr)",
    minWidth: 0,
  },
  dragHandle: {
    alignItems: "center",
    color: "#94a3b8",
    cursor: "grab",
    display: "inline-flex",
    justifyContent: "center",
  },
  queueNumber: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dbe5dd",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 950,
    height: 34,
    justifyContent: "center",
    width: 34,
  },
  queueCopy: {
    display: "grid",
    gap: 4,
    minWidth: 0,
  },
  queueTitle: {
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 950,
    lineHeight: 1.25,
    overflowWrap: "anywhere",
  },
  queueMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.35,
    overflowWrap: "anywhere",
  },
  slideActions: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
  },
  previewControls: {
    alignItems: "center",
    display: "flex",
    gap: 8,
  },
  previewControlsPhone: {
    alignItems: "stretch",
    flexWrap: "wrap",
  },
  slideCounter: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 900,
  },
  screenPreview: {
    alignItems: "center",
    background: "linear-gradient(180deg, #0f172a, #1e293b)",
    borderRadius: 20,
    display: "flex",
    justifyContent: "center",
    minHeight: 500,
    overflow: "hidden",
    padding: 24,
  },
  screenPreviewPhone: {
    minHeight: 320,
    padding: 16,
  },
  controllerScreen: {
    alignItems: "center",
    background: "linear-gradient(180deg, #0f172a, #1e293b)",
    borderRadius: 22,
    display: "flex",
    justifyContent: "center",
    minHeight: 560,
    overflow: "hidden",
    padding: 24,
  },
  controllerScreenPhone: {
    minHeight: 360,
    padding: 16,
  },
  controllerBottomControls: {
    background: "rgba(255,255,255,0.92)",
    border: "1px solid #d8e5ef",
    borderRadius: 20,
    boxShadow: "0 16px 44px rgba(15,23,42,0.12)",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "minmax(220px, 1fr) auto minmax(220px, 1fr)",
    padding: 8,
  },
  controllerBottomControlsPhone: {
    gridTemplateColumns: "1fr",
  },
  controllerBottomButton: {
    alignItems: "center",
    background: "#0f172a",
    border: "none",
    borderRadius: 22,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: "clamp(18px, 1.8vw, 24px)",
    fontWeight: 950,
    gap: 12,
    justifyContent: "center",
    lineHeight: 1,
    minHeight: 58,
    padding: "12px 32px",
    width: "100%",
  },
  controllerBottomPrimaryButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "1px solid rgba(92, 125, 83, 0.62)",
    borderRadius: 999,
    boxShadow: "0 14px 34px rgba(92, 125, 83, 0.24)",
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 14,
    fontWeight: 950,
    gap: 8,
    justifyContent: "center",
    lineHeight: 1.1,
    minHeight: 58,
    minWidth: 220,
    padding: "12px 18px",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  previewEmpty: {
    color: "#ffffff",
    fontSize: 18,
    fontWeight: 900,
  },
  titleSlide: {
    alignItems: "center",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    justifyContent: "center",
    minHeight: 452,
    textAlign: "center",
  },
  titleHeadline: {
    fontSize: "clamp(36px, 4vw, 56px)",
    fontWeight: 900,
    lineHeight: 1.05,
  },
  titleSubline: {
    fontSize: 22,
    opacity: 0.92,
  },
  titleDate: {
    fontSize: 16,
    opacity: 0.74,
  },
  verseSlide: {
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: 18,
    justifyContent: "center",
    minHeight: 452,
  },
  verseReference: {
    color: "#cfe1c5",
    fontSize: 18,
    fontWeight: 900,
  },
  verseText: {
    display: "flex",
    fontSize: "clamp(28px, 2.8vw, 40px)",
    gap: 14,
    lineHeight: 1.5,
  },
  verseNumber: {
    color: "#9ed08a",
    fontSize: 16,
    fontWeight: 900,
    minWidth: 22,
    paddingTop: 8,
  },
  customSlide: {
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: 18,
    justifyContent: "center",
    minHeight: 452,
  },
  customTitle: {
    fontSize: "clamp(34px, 3vw, 46px)",
    fontWeight: 900,
    lineHeight: 1.1,
  },
  customBody: {
    color: "#e2e8f0",
    fontSize: "clamp(22px, 2vw, 30px)",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
  },
  mediaSlide: {
    borderRadius: 18,
    display: "block",
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
    width: "100%",
  },
};
